// Nén ảnh ngay trên trình duyệt trước khi gửi lên API.
//
// Trước đây tệp người dùng chọn được gửi nguyên gốc: ảnh chụp từ điện thoại
// thường 3-5 MB, vừa tốn băng thông vừa làm khung xem ảnh phình to.
//
// Ảnh đi vào email báo giá (CaseRepository dựng thẻ <img>) phải giữ JPEG, vì
// Outlook trên Windows dùng engine của Word và không đọc được WebP. Những ảnh
// chỉ hiển thị trong web/app thì dùng WebP cho nhẹ hơn khoảng 25%.

export const IMAGE_MAX_EDGE = 1600;
export const IMAGE_QUALITY = 0.8;

export type ImageFormat = 'webp' | 'jpeg';

let webpSupported: boolean = null;

// Trình duyệt không hỗ trợ sẽ lặng lẽ trả về PNG thay vì WebP, nên phải thử
// thật rồi đọc tiền tố chuỗi chứ không tra theo tên trình duyệt.
export function supportsWebp(): boolean {
  if (webpSupported === null) {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    webpSupported = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }
  return webpSupported;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('cannot decode image'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
  return new Promise(resolve => {
    // toBlob không có trong lib.dom của TypeScript 3.1 nên phải ép kiểu.
    const anyCanvas = canvas as any;
    if (typeof anyCanvas.toBlob === 'function') {
      anyCanvas.toBlob((blob: Blob) => resolve(blob), mime, quality);
      return;
    }
    // Safari cũ: dựng lại Blob từ data URL.
    const parts = canvas.toDataURL(mime, quality).split(',');
    const binary = atob(parts[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    resolve(new Blob([bytes], { type: mime }));
  });
}

function renameTo(name: string, ext: string): string {
  const dot = name.lastIndexOf('.');
  return (dot > 0 ? name.substring(0, dot) : name) + '.' + ext;
}

/**
 * Thu nhỏ và nén lại ảnh. Trả về chính tệp gốc nếu không nén được hoặc nén
 * xong còn nặng hơn - ảnh nhỏ sẵn, hoặc ảnh đã tối ưu, thường rơi vào nhóm này.
 *
 * GIF và SVG được giữ nguyên: vẽ lên canvas sẽ mất ảnh động, còn SVG là vector
 * nên vốn đã nhẹ.
 */
export async function compressImage(
  file: File,
  format: ImageFormat = 'webp',
  maxEdge: number = IMAGE_MAX_EDGE,
  quality: number = IMAGE_QUALITY
): Promise<File> {
  if (!file || file.type.indexOf('image/') !== 0) { return file; }
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') { return file; }

  const mime = format === 'webp' && supportsWebp() ? 'image/webp' : 'image/jpeg';
  const ext = mime === 'image/webp' ? 'webp' : 'jpg';

  try {
    const img = await loadImage(file);
    const ratio = Math.min(1, maxEdge / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * ratio);
    canvas.height = Math.round(img.height * ratio);

    const ctx = canvas.getContext('2d');
    // JPEG và WebP không có kênh alpha; nền mặc định của canvas là trong suốt
    // nên ảnh PNG có nền trong sẽ ra nền đen nếu không tô trắng trước.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await canvasToBlob(canvas, mime, quality);
    if (!blob || blob.size >= file.size) { return file; }

    // File constructor không có trên IE/Edge cũ; Blob kèm tên vẫn gửi được qua
    // FormData nên dùng làm phương án dự phòng.
    const name = renameTo(file.name, ext);
    try {
      return new File([blob], name, { type: mime });
    } catch (e) {
      const fallback = blob as any;
      fallback.name = name;
      return fallback as File;
    }
  } catch (e) {
    return file;
  }
}
